import {
    FETCH_MILES,
    FETCH_MILES_SUCCESS,
    FETCH_MILES_FAILED, TRANSFER_MILES, TRANSFER_MILES_SUCCESS, TRANSFER_MILES_FAILED, RESET_TRANSFER_STATUS,
    FETCH_AIRTIME_STOCK, FETCH_AIRTIME_STOCK_SUCCESS, FETCH_AIRTIME_STOCK_FAILED,
    REDEEM_AIRTIME, REDEEM_AIRTIME_SUCCESS, REDEEM_AIRTIME_FAILED, RESET_AIRTIME_STATUS,
    DONATE_MILES, DONATE_MILES_SUCCESS, DONATE_MILES_FAILED, RESET_DONATE_STATUS,
} from '../store/types';

import { firebase } from '../config/configureFirebase';
import i18n from "i18n-js";
import {getDatabase, get, ref, query as dbQuery, orderByChild, equalTo} from 'firebase/database';
import store from "../store/store";

// Canonical provider slugs — must match PHONE_CARD_PROVIDERS in
// functions/index.js and the /phone_cards RTDB partitioning.
export const AIRTIME_PROVIDERS = [
    { key: 'ethiotel', labelKey: 'ethio_telecom' },
    { key: 'safaricom', labelKey: 'safaricom' },
];

const deriveHost = (settings, config) =>
    (typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin)
        ? window.location.origin
        : `https://${config.projectId}.web.app`;

// Authenticated POST to a redemption Cloud Function. The server derives the
// caller from the Bearer token — no uid travels in the body. Exported for
// sibling action files (paymentactions' wallet redemption uses it too).
export const postWithIdToken = async (path, body) => {
    const { auth, config } = firebase;
    const user = auth?.currentUser;
    if (!user) {
        const err = new Error('Not signed in');
        err.code = 'not_signed_in';
        throw err;
    }
    // forceRefresh=false — cached token is reused (~1h) and auto-refreshes.
    const idToken = await user.getIdToken(false);
    const settings = store.getState().settingsdata.settings;
    const res = await fetch(`${deriveHost(settings, config)}${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + idToken,
        },
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) {
        const err = new Error(json.error || json.message || `Request failed (${res.status})`);
        err.code = json.code || null;
        err.payload = json;
        throw err;
    }
    return json;
};

// Exported so screens can mint ONE id per user intent and reuse it across
// transport-level retries — that reuse is what makes the server's replay
// protection actually engage (a fresh id per attempt would double-charge).
export const makeMileRequestId = () =>
    'rq' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);

export const fetchUserMiles = () => async (dispatch) => {

    const {
        auth,
        donkeyMilesHistoryRef } = firebase;

    dispatch({ type: FETCH_MILES, payload: null });

    const uid = auth.currentUser?.uid;

    try {
        const snapshot = await get(donkeyMilesHistoryRef(uid));
        if(snapshot.exists()){
            const data = snapshot.val();
            const arr = Object.keys(data).map(i => {
                data[i].id = i
                return data[i]
            });
            dispatch({
                type: FETCH_MILES_SUCCESS,
                payload: arr.reverse()
            });
        } else {
            dispatch({
                type: FETCH_MILES_FAILED,
                payload: "No data available."
            });
        }
    } catch (error) {
        dispatch({
            type: FETCH_MILES_FAILED,
            payload: error.message
        });
    }
};

export const transferDonkeyMiles = (phoneNumber, donkeyMiles, role) => {
    return async (dispatch) => {
        const { t } = i18n;
        const settings = store.getState().settingsdata.settings;
        dispatch({ type: TRANSFER_MILES, payload: null });

        const decimal = settings.decimal;
        const milesToSend = parseFloat(donkeyMiles).toFixed(decimal);
        if (!milesToSend || milesToSend <= 0) {
            dispatch({ type: TRANSFER_MILES_FAILED, payload: t('invalid_miles_value') });
            return;
        }

        try {
            // Find recipient UID by phone number
            const db = getDatabase();
            const formattedPhone = `+251${phoneNumber.trim()}`;
            const usersQuery = dbQuery(ref(db, 'users'), orderByChild('mobile'), equalTo(formattedPhone));
            const snapshot = await get(usersQuery);
            const recipientUid = snapshot.exists() ? Object.keys(snapshot.val())[0] : null;

            if (!recipientUid) {
                dispatch({ type: TRANSFER_MILES_FAILED, payload: t('user_not_found') });
                return;
            }

            // The new Cloud Function reads the sender from the Bearer token
            // (it 403s if a body senderUid mismatches). senderUid is still
            // sent because the PRE-hardening server requires it — this keeps
            // the OTA bundle compatible with both server versions during the
            // deploy window.
            const senderUid = firebase.auth?.currentUser?.uid;
            const json = await postWithIdToken('/transfer_donkey_miles', { senderUid, recipientUid, milesToSend, role });
            dispatch({ type: TRANSFER_MILES_SUCCESS, payload: json });
        } catch (error) {
            dispatch({ type: TRANSFER_MILES_FAILED, payload: error.code === 'not_signed_in' ? t('user_not_logged_in') : (error.payload?.message || 'Miles transfer failed') });
        }
    };
};

export const resetTransferStatus = () => ({
    type: RESET_TRANSFER_STATUS,
});

// One-shot read of the tiny /phone_cards_meta counters node — never the
// card pool. Missing node resolves to {} (everything shows out of stock).
export const fetchAirtimeStock = () => async (dispatch) => {
    const { phoneCardsMetaRef } = firebase;
    dispatch({ type: FETCH_AIRTIME_STOCK, payload: null });
    try {
        const snapshot = await get(phoneCardsMetaRef);
        const raw = snapshot.val() || {};
        const stock = {};
        Object.keys(raw).forEach((provider) => {
            stock[provider] = {};
            Object.keys(raw[provider] || {}).forEach((value) => {
                const meta = raw[provider][value] || {};
                stock[provider][value] = {
                    available: parseInt(meta.available, 10) || 0,
                    used: parseInt(meta.used, 10) || 0,
                };
            });
        });
        dispatch({ type: FETCH_AIRTIME_STOCK_SUCCESS, payload: stock });
    } catch (error) {
        dispatch({ type: FETCH_AIRTIME_STOCK_FAILED, payload: error.message });
    }
};

// requestId: mint via makeMileRequestId() ONCE per user intent (the screen
// holds it in a ref) and pass the same id on retries after network failures.
export const redeemAirtime = (provider, value, requestId) => async (dispatch) => {
    dispatch({ type: REDEEM_AIRTIME, payload: null });
    try {
        const json = await postWithIdToken('/redeem_airtime', {
            provider,
            value,
            requestId: requestId || makeMileRequestId(),
        });
        dispatch({
            type: REDEEM_AIRTIME_SUCCESS,
            payload: {
                provider,
                value,
                code: json.code || null,
                milesDeducted: json.milesDeducted,
                smsSent: json.smsSent !== false,
            },
        });
        // Counters and history both changed server-side.
        dispatch(fetchAirtimeStock());
        dispatch(fetchUserMiles());
    } catch (error) {
        dispatch({
            type: REDEEM_AIRTIME_FAILED,
            payload: { code: error.code || (error.message === 'Network request failed' ? 'network' : null), message: error.message, details: error.payload || null },
        });
        if (error.code === 'out_of_stock') {
            dispatch(fetchAirtimeStock());
        }
    }
};

export const resetAirtimeStatus = () => ({ type: RESET_AIRTIME_STATUS });

export const donateMiles = (ngoId, miles, requestId) => async (dispatch) => {
    dispatch({ type: DONATE_MILES, payload: null });
    try {
        const json = await postWithIdToken('/donate_miles', { ngoId, miles: Number(miles), requestId: requestId || makeMileRequestId() });
        dispatch({
            type: DONATE_MILES_SUCCESS,
            payload: { ngoId, ngo: json.ngo, miles: json.milesDonated },
        });
        dispatch(fetchUserMiles());
    } catch (error) {
        dispatch({
            type: DONATE_MILES_FAILED,
            payload: { code: error.code || null, message: error.message },
        });
    }
};

export const resetDonateStatus = () => ({ type: RESET_DONATE_STATUS });
